import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface PriceAnalysis {
  id: string
  product: string
  current_price: number
  optimal_price: number
  price_elasticity: number
  margin: number
  conversion_rate: number
  recommendation: string
}

interface CompetitorPricing {
  id: string
  competitor: string
  basic_price: number
  pro_price: number
  enterprise_price: number
  price_position: string
  market_share: number
}

interface PricingExperiment {
  id: string
  name: string
  status: string
  variant_a_price: number
  variant_b_price: number
  variant_a_conversion: number
  variant_b_conversion: number
  winner: string
}

interface Statistics {
  avg_price: number
  price_vs_market: number
  optimal_price_gap: number
  pricing_experiments: number
  competitor_monitored: number
  revenue_from_optimization: number
}

export default function PricingAnalyticsPage() {
  const [analysis, setAnalysis] = useState<PriceAnalysis[]>([])
  const [competitors, setCompetitors] = useState<CompetitorPricing[]>([])
  const [experiments, setExperiments] = useState<PricingExperiment[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [analysisRes, competitorsRes, experimentsRes, statsRes] = await Promise.all([
        fetch('/api/pricinganalytics/analysis'),
        fetch('/api/pricinganalytics/competitors'),
        fetch('/api/pricinganalytics/experiments'),
        fetch('/api/pricinganalytics/statistics')
      ])
      if (analysisRes.ok) setAnalysis(await analysisRes.json())
      if (competitorsRes.ok) setCompetitors(await competitorsRes.json())
      if (experimentsRes.ok) setExperiments(await experimentsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching pricing analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getRecommendationBadge = (rec: string) => {
    if (rec === 'increase') return 'bg-green-100 text-green-800'
    if (rec === 'decrease') return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  const getStatusBadge = (status: string) => {
    if (status === 'active') return 'bg-green-100 text-green-800'
    if (status === 'completed') return 'bg-blue-100 text-blue-800'
    return 'bg-yellow-100 text-yellow-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Pricing Analytics</h1>
          <p className="mt-2 text-gray-600">Phase 232 - Price analysis, optimization, and competitor pricing</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Price</p>
              <p className="text-2xl font-bold text-gray-900">${statistics.avg_price}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">vs Market</p>
              <p className={`text-2xl font-bold ${statistics.price_vs_market >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {statistics.price_vs_market > 0 ? '+' : ''}{statistics.price_vs_market}%
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Optimal Gap</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.optimal_price_gap}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Experiments</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.pricing_experiments}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Competitors</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.competitor_monitored}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Optimization Revenue</p>
              <p className="text-2xl font-bold text-green-600">${(statistics.revenue_from_optimization/1000).toFixed(0)}K</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Price Analysis</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Optimal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Elasticity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Margin</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversion</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recommendation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {analysis.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{item.product}</td>
                    <td className="px-6 py-4 text-gray-500">${item.current_price}</td>
                    <td className="px-6 py-4 text-green-600 font-medium">${item.optimal_price}</td>
                    <td className="px-6 py-4 text-gray-500">{item.price_elasticity}</td>
                    <td className="px-6 py-4 text-blue-600">{item.margin}%</td>
                    <td className="px-6 py-4 text-purple-600">{item.conversion_rate}%</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getRecommendationBadge(item.recommendation)}`}>
                        {item.recommendation}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Competitor Pricing</h2>
            </div>
            <div className="p-6 space-y-4">
              {competitors.map((comp) => (
                <div key={comp.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{comp.competitor}</span>
                    <span className={`text-sm ${comp.price_position === 'lower' ? 'text-red-600' : comp.price_position === 'higher' ? 'text-green-600' : 'text-gray-600'}`}>
                      {comp.price_position}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Basic</p>
                      <p className="font-medium text-gray-900">${comp.basic_price}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Pro</p>
                      <p className="font-medium text-blue-600">${comp.pro_price}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Enterprise</p>
                      <p className="font-medium text-purple-600">${comp.enterprise_price}</p>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">Market Share: {comp.market_share}%</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Pricing Experiments</h2>
            </div>
            <div className="p-6 space-y-4">
              {experiments.map((exp) => (
                <div key={exp.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{exp.name}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(exp.status)}`}>
                      {exp.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="p-2 bg-white rounded">
                      <p className="text-gray-500">Variant A: ${exp.variant_a_price}</p>
                      <p className="font-medium text-blue-600">{exp.variant_a_conversion}% conversion</p>
                    </div>
                    <div className="p-2 bg-white rounded">
                      <p className="text-gray-500">Variant B: ${exp.variant_b_price}</p>
                      <p className="font-medium text-purple-600">{exp.variant_b_conversion}% conversion</p>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">Winner: <span className="font-medium text-green-600">{exp.winner}</span></p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
